import React, { useEffect, useMemo, useRef } from 'react';
import {
    RichText,
    useEditorBridge,
    useEditorContent,
    DEFAULT_TOOLBAR_ITEMS,
    TenTapStartKit,
    CoreBridge,
    useBridgeState,
    Images,
    ToolbarItem,
    EditorBridge,
} from '@10play/tentap-editor';
import Text, { textVariantStyleMap } from '../../Text';
import Stack from '../../Stack';
import { useElementPosition } from '../../../../hooks/useElementPosition';
import { injectedFontFamilyCss } from './injectedFontFamilyCss';
import { StyleProp, TextInput, View } from 'react-native';
import { fontWeightToFontFamilyName } from '../../../../hooks/useLoadAssets';
import { innerTextInputStyles } from '../InputBase';

type IsEditingNoteContentContextValue = {
    editor?: EditorBridge;
    toolbarItems?: ToolbarItem[]; // Add toolbar items to context
};

export const IsEditingNoteContentContext = React.createContext<IsEditingNoteContentContextValue>({});

export const IsEditingNoteContentProvider: React.FC<{
    children: (
        renderArgs: IsEditingNoteContentContextValue & {
            setEditor: (editor: any) => void;
            setToolbarItems: (items: ToolbarItem[]) => void;
        },
    ) => React.ReactNode;
}> = ({ children }) => {
    const textInputRef = React.useRef<TextInput>(null);
    const [editor, setEditor] = React.useState<EditorBridge | undefined>(undefined);
    const [toolbarItems, setToolbarItems] = React.useState<ToolbarItem[]>([]);

    const contextValue = React.useMemo<IsEditingNoteContentContextValue>(
        () => ({
            editor,
            toolbarItems,
        }),
        [editor, toolbarItems],
    );

    return (
        <IsEditingNoteContentContext.Provider value={contextValue}>
            <>
                <TextInput ref={textInputRef} style={{ display: 'none' }} />
                {children({ ...contextValue, setEditor, setToolbarItems })}
            </>
        </IsEditingNoteContentContext.Provider>
    );
};

export type NoteContentInputOwnProps = {
    placeholder: string;
    error?: string;
    value: string | null;
    onChange: (value: string) => void;
    onBlur?: () => void;
    onFocus?: () => void;
    isDisabled?: boolean;
    testID?: string;
    showToolbar?: boolean;
};

export type NoteContentInputProps = NoteContentInputOwnProps;

function convertStyleToCSS(styleObject: StyleProp<any>, keepFontFamily = false) {
    return Object.keys(styleObject)
        .filter((_) => _ !== 'fontFamily' || keepFontFamily)
        .reduce((css, key) => {
            const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            const value = typeof styleObject[key] === 'number' ? `${styleObject[key]}px` : styleObject[key];
            return `${css}${cssKey}: ${value};\n`;
        }, '');
}

const addStyleToSheet = (htmlTag: string, style: StyleProp<any>) => {
    const css = convertStyleToCSS(style);
    return `${htmlTag} { ${css} }`;
};

const customFont = `
${injectedFontFamilyCss}
* {
    font-family: 'Overpass', sans-serif;
}
${addStyleToSheet('h1', textVariantStyleMap['h1'])}
${addStyleToSheet('h2', textVariantStyleMap['h2'])}
${addStyleToSheet('h3', textVariantStyleMap['h3'])}
${addStyleToSheet('h4', textVariantStyleMap['h4'])}
${addStyleToSheet('h5', textVariantStyleMap['h5'])}
${addStyleToSheet('h6', textVariantStyleMap['h6'])}
${addStyleToSheet('p', textVariantStyleMap['body-1'])}
${addStyleToSheet('span', textVariantStyleMap['body-1'])}
${addStyleToSheet('strong', {
    fontFamily: fontWeightToFontFamilyName.Overpass['semi-bold'],
    fontWeight: '600',
})}
`;

// this is hacky, but seemingly the only way to filter out toolbar types
let displayItems: ToolbarItem[] = DEFAULT_TOOLBAR_ITEMS;
try {
    const disabledImages = [Images.checkList, Images.code];
    displayItems = DEFAULT_TOOLBAR_ITEMS.filter((item) => !disabledImages.includes((item as any)?.image?.()));
} catch (e) {
    console.error('Error filtering out checkList from toolbar items');
}

const NoteContentInput: React.FC<
    NoteContentInputProps & {
        setEditor?: (editor: any) => void;
        setToolbarItems?: (items: ToolbarItem[]) => void;
    }
> = ({ onChange, value, onFocus, onBlur, setEditor, setToolbarItems, isDisabled, error }) => {
    const hasBeenFocused = useRef(false);

    const bridgeExtensions = useMemo(() => [...TenTapStartKit, CoreBridge.configureCSS(customFont)], []);

    const editor = useEditorBridge({
        initialContent: value || '',
        bridgeExtensions,
        theme: {
            toolbar: {
                linkBarTheme: {
                    linkInput: innerTextInputStyles,
                    addLinkContainer: {
                        width: '100%',
                    },
                },
            },
        },
        autofocus: false,
    });

    useEffect(() => {
        if (setEditor) {
            setEditor(editor);
        }
        if (setToolbarItems) {
            setToolbarItems(displayItems);
        }
    });

    const bridgeState = useBridgeState(editor);
    useEffect(() => {
        if (bridgeState?.isFocused) {
            if (!hasBeenFocused.current) {
                onFocus?.();
                hasBeenFocused.current = true;
            }
        } else if (hasBeenFocused.current) {
            onBlur?.();
        }
    }, [bridgeState?.isFocused, onFocus, onBlur]);

    const content = useEditorContent(editor);
    useEffect(() => {
        if (content !== undefined && content !== value) {
            onChange(content.toString());
        }
    }, [content, value, onChange]);

    const { ref, position, onLayout } = useElementPosition({
        clearPositionOnDisable: true,
    });

    return (
        <Stack
            vertical
            fullWidth
            align="flex-start"
            flex={1}
            style={{ alignSelf: 'stretch' }}
            onLayout={onLayout}
            ref={ref}
            testID="note-input-root"
        >
            <View style={{ flex: 1, alignSelf: 'stretch', display: position?.width ? undefined : 'none' }}>
                <RichText
                    editor={editor}
                    aria-disabled={isDisabled}
                    style={{
                        width: position?.width,
                        flex: 1,
                        alignSelf: 'stretch',
                    }}
                />
                {error ? <Text variant="input-error">{error}</Text> : null}
            </View>
        </Stack>
    );
};

export default React.memo(NoteContentInput);
